import React, { useCallback } from 'react';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useTheme } from '@material-ui/core/styles';
import styled from 'styled-components';
import { StyledDataTableRowHoverButton } from './DataTableBodyCell/DataTableRowHoverButton';
import DataTableBodyCellCheckbox from './DataTableBodyCell/DataTableBodyCellCheckbox';

const StyledTableBody = styled(TableBody)`
  tr:hover ${StyledDataTableRowHoverButton} {
    visibility: visible;
  }
`;

const DataTableBody = ({ data, columns, loading, error, lineHover = false, onLineClick = () => {}, showCheckbox, checkboxLines, setCheckboxLines }) => {
  const showNothingToDisplay = data.length === 0 && !loading && !error;
  const showError = !!error;
  const showBody = data.length > 0 && !error;

  const theme = useTheme();
  return (
    <StyledTableBody>
      {showNothingToDisplay && <BodyNothingToDisplay columnsCount={columns.length} rowHeight={theme.custom.dataTable.placeholderLineHeight} />}
      {showError && <BodyError columnsCount={columns.length} rowHeight={theme.custom.dataTable.placeholderLineHeight} />}
      {showBody && (
        <BodyData
          columnsCount={columns.length}
          lineHover={lineHover}
          onLineClick={onLineClick}
          data={data}
          columns={columns}
          showCheckbox={showCheckbox}
          checkboxLines={checkboxLines}
          setCheckboxLines={setCheckboxLines}
        />
      )}
    </StyledTableBody>
  );
};

const columnShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  align: PropTypes.string,
});

DataTableBody.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.arrayOf(columnShape),
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  lineHover: PropTypes.bool,
  lineCount: PropTypes.number,
  onLineClick: PropTypes.func,
  showCheckbox: PropTypes.bool,
  checkboxLines: PropTypes.array,
  setCheckboxLines: PropTypes.func,
};

export default DataTableBody;

const BodyNothingToDisplay = ({ columnsCount = 1, rowHeight }) => (
  <TableRow>
    <TableCell colSpan={columnsCount} align={'center'} style={{ height: rowHeight }}>
      <FormattedMessage id={'app.dataTable.body.noData'} />
    </TableCell>
  </TableRow>
);

BodyNothingToDisplay.propTypes = {
  columnsCount: PropTypes.number,
  rowHeight: PropTypes.string,
};

const BodyError = ({ columnsCount = 1, rowHeight }) => (
  <TableRow>
    <TableCell colSpan={columnsCount} align={'center'} style={{ height: rowHeight }}>
      <FormattedMessage id={'app.dataTable.body.error'} />
    </TableCell>
  </TableRow>
);

BodyError.propTypes = {
  columnsCount: PropTypes.number,
  rowsPerPage: PropTypes.number,
  rowHeight: PropTypes.string,
  error: PropTypes.string,
};

const BodyData = ({ lineHover, onLineClick, data, columns, showCheckbox, checkboxLines, setCheckboxLines }) => {
  const toggleChecked = useCallback(
    async (id) => {
      let output = [...checkboxLines];
      if (output.indexOf(id) === -1) {
        output.push(id);
      } else {
        output = output.filter((value) => {
          return value !== id;
        });
      }
      setCheckboxLines(output);
    },
    [setCheckboxLines, checkboxLines],
  );

  return (
    <>
      {data.map((row, index) => (
        <TableRow hover={lineHover} key={index} onClick={() => onLineClick(row, index)}>
          {showCheckbox && (
            <DataTableBodyCellCheckbox
              checked={checkboxLines?.indexOf(row.id) > -1}
              toggleChecked={async () => {
                await toggleChecked(row.id);
              }}
            />
          )}
          {columns.map((column) => (
            <TableCell align={column.align} key={column.id}>
              {row[column.id]}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};

BodyData.propTypes = {
  lineHover: PropTypes.bool,
  onLineClick: PropTypes.func,
  data: PropTypes.array,
  columns: PropTypes.array,
  error: PropTypes.string,
  showCheckbox: PropTypes.bool,
  checkboxLines: PropTypes.array,
  setCheckboxLines: PropTypes.func,
};
